import { appState } from '../state';
import { icon, refreshIcons } from '../icons';
import { migrateFromV1, type OnboardingChoice } from '@/storage/migrateFromV1';
import { metaGet } from '@/storage/repo';

export function renderOnboarding(root: HTMLElement) {
  root.innerHTML = `
    <section class="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div class="text-center mb-10">
        <h2 class="text-2xl sm:text-3xl font-bold tracking-tight">Welcome to Budget Tracker v2</h2>
        <p class="mt-2 text-slate-500 dark:text-slate-400">Pick how you want to start. You can change this later in Settings.</p>
        <p id="v1Note" class="mt-3 text-xs text-slate-400 hidden">We found v1 data in your browser — it'll be imported automatically.</p>
      </div>

      <div class="grid sm:grid-cols-3 gap-4">
        <button data-choice="defaults" class="onboard-tile text-left p-5 rounded-2xl border-2 border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 hover:border-indigo-500 hover:shadow-md transition">
          <div class="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 flex items-center justify-center mb-3">${icon('layout-template', 'w-5 h-5')}</div>
          <h3 class="font-semibold mb-1">Use defaults</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400">Pre-loads the March 2026 budget with the 14 items you started v1 with.</p>
        </button>

        <button data-choice="empty" class="onboard-tile text-left p-5 rounded-2xl border-2 border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 hover:border-indigo-500 hover:shadow-md transition">
          <div class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300 flex items-center justify-center mb-3">${icon('file-plus', 'w-5 h-5')}</div>
          <h3 class="font-semibold mb-1">Start empty</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400">Begin with no items. Add expenses manually as you go.</p>
        </button>

        <button data-choice="import" class="onboard-tile text-left p-5 rounded-2xl border-2 border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 hover:border-indigo-500 hover:shadow-md transition">
          <div class="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 flex items-center justify-center mb-3">${icon('upload-cloud', 'w-5 h-5')}</div>
          <h3 class="font-semibold mb-1">Import a statement</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400">Upload a Capitec or TymeBank statement — we'll suggest budget items from it.</p>
        </button>
      </div>
    </section>
  `;
  refreshIcons();

  // Show v1-import note if applicable
  try {
    const v1 = localStorage.getItem('budgetTracker.v1');
    if (v1) document.getElementById('v1Note')?.classList.remove('hidden');
  } catch {}

  document.querySelectorAll<HTMLButtonElement>('.onboard-tile').forEach(btn => {
    btn.addEventListener('click', async () => {
      const choice = btn.dataset.choice as OnboardingChoice;
      btn.disabled = true;
      btn.classList.add('opacity-50');
      await migrateFromV1(choice);

      // Load persisted state into appState
      const [income, currentMonth, theme, filter, amountsHidden] = await Promise.all([
        metaGet('income'), metaGet('currentMonth'), metaGet('theme'),
        metaGet('filter'), metaGet('amountsHidden'),
      ]);

      appState.set(s => ({
        ...s,
        onboardingDone: true,
        income: income ? Number(income) : s.income,
        currentMonth: currentMonth || s.currentMonth,
        theme: (theme as any) || s.theme,
        filter: (filter as any) || s.filter,
        amountsHidden: amountsHidden === '1',
      }));

      if (choice === 'import') {
        location.hash = '#/upload';
      } else {
        location.hash = '#/home';
      }
    });
  });
}
